import { TEST_RUN_EMPTY_STATE } from './defaults';
import {
  getHydratedCustomFunctionState,
  getNewTestStringWithEnvVars,
} from './helpers';
import type {
  CustomFunctionModel,
  EnvironmentVariable,
  TestRun,
} from './types';

/** Enum defining all possible action types for the custom function state reducer */
export enum CustomFunctionActionType {
  UPDATE_CODE = 'UPDATE_CODE',
  UPDATE_TEST_JSON = 'UPDATE_TEST_JSON',
  UPDATE_ENV_VAR = 'UPDATE_ENV_VAR',
  TOGGLE_VISIBILITY_ENV_VAR = 'TOGGLE_VISIBILITY_ENV_VAR',
  ADD_ENV_VAR = 'ADD_ENV_VAR',
  REMOVE_ENV_VAR = 'REMOVE_ENV_VAR',
  START_TEST = 'START_TEST',
  UPDATE_TEST_RUN = 'UPDATE_TEST_RUN',
  CLEAR_TEST_RUN = 'CLEAR_TEST_RUN',
  HYDRATE_CUSTOM_FUNCTION = 'HYDRATE_CUSTOM_FUNCTION',
}

export interface CustomFunctionCompleteState {
  /** The Deno function code */
  code: string;
  /** The environment variables */
  envVars: EnvironmentVariable[];
  /** The test payload JSON string */
  testJson: string;
  /** Test run metadata */
  testRun: TestRun;
}

/**
 * Action types
 */
export type CustomFunctionStateAction =
  | {
      /** Update the code in the Code Editor tab */
      type: CustomFunctionActionType.UPDATE_CODE;
      /** the new code */
      code: string;
    }
  | {
      /** Update the test JSON in the Test tab */
      type: CustomFunctionActionType.UPDATE_TEST_JSON;
      /** the new test JSON */
      testJson: string;
    }
  | {
      /** Update an environment variable name or value */
      type: CustomFunctionActionType.UPDATE_ENV_VAR;
      /** environment variable element index */
      index: number;
      /** the field to update */
      field: 'name' | 'value';
      /** the new value */
      value: string;
    }
  | {
      /** Toggle the visibility of an environment variable */
      type: CustomFunctionActionType.TOGGLE_VISIBILITY_ENV_VAR;
      /** environment variable element index */
      index: number;
    }
  | {
      /** Add a new environment variable */
      type: CustomFunctionActionType.ADD_ENV_VAR;
    }
  | {
      /** Remove an environment variable */
      type: CustomFunctionActionType.REMOVE_ENV_VAR;
      /** environment variable element index */
      index: number;
    }
  | {
      /** Run a test */
      type: CustomFunctionActionType.START_TEST;
    }
  | {
      /** Update the test run */
      type: CustomFunctionActionType.UPDATE_TEST_RUN;
      /** The new test run */
      testRun: TestRun;
    }
  | {
      /** Clear the test run */
      type: CustomFunctionActionType.CLEAR_TEST_RUN;
    }
  | {
      /** Update the custom function */
      type: CustomFunctionActionType.HYDRATE_CUSTOM_FUNCTION;
      /** The new custom function */
      customFunctionModel: CustomFunctionModel;
    };

// Reducer function
export const customFunctionReducer = (
  state: CustomFunctionCompleteState,
  action: CustomFunctionStateAction,
): CustomFunctionCompleteState => {
  switch (action.type) {
    case CustomFunctionActionType.UPDATE_CODE:
      return { ...state, code: action.code };
    case CustomFunctionActionType.UPDATE_TEST_JSON:
      return { ...state, testJson: action.testJson };
    case CustomFunctionActionType.TOGGLE_VISIBILITY_ENV_VAR: {
      const newState = { ...state };
      newState.envVars[action.index] = {
        ...newState.envVars[action.index],
        isSecret: !newState.envVars[action.index].isSecret,
      };
      return newState;
    }
    case CustomFunctionActionType.UPDATE_ENV_VAR: {
      const newState = { ...state };
      // Update this env var depending on the field
      newState.envVars[action.index] = {
        ...newState.envVars[action.index],
        [action.field]: action.value,
      };
      if (action.field === 'name') {
        // Also update the test JSON
        newState.testJson = getNewTestStringWithEnvVars(newState);
      }
      return newState;
    }
    case CustomFunctionActionType.ADD_ENV_VAR:
      return {
        ...state,
        envVars: [...state.envVars, { name: '', value: '', isSecret: false }],
      };
    case CustomFunctionActionType.REMOVE_ENV_VAR: {
      const { envVars, ...rest } = { ...state };
      envVars.splice(action.index, 1);
      const newState = {
        ...rest,
        // Updated env vars, post splice
        envVars,
        // Also update the test JSON
        testJson: getNewTestStringWithEnvVars({ ...rest, envVars }),
      };
      return newState;
    }
    case CustomFunctionActionType.START_TEST: {
      return {
        ...state,
        testRun: {
          ...TEST_RUN_EMPTY_STATE,
          status: 'pending',
        },
      };
    }
    case CustomFunctionActionType.UPDATE_TEST_RUN: {
      return {
        ...state,
        testRun: {
          ...state.testRun,
          ...action.testRun,
        },
      };
    }
    case CustomFunctionActionType.CLEAR_TEST_RUN: {
      return {
        ...state,
        testRun: TEST_RUN_EMPTY_STATE,
      };
    }
    case CustomFunctionActionType.HYDRATE_CUSTOM_FUNCTION: {
      return getHydratedCustomFunctionState(action.customFunctionModel);
    }
    default:
      return state;
  }
};
